import { useEffect, useState } from "react";

import { toCount } from "common/utils/formatting";

import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import Accordion from "client/common/components/interactive/Accordion";

import { PRODUCT_FILTER_DEFINITIONS } from "../constants/filterDefinitions";
import { FilterValue, SelectedProductFilters, ProductSearchFilters } from "../types";
import SearchFilter from "./SearchFilter";

type ProductSearchFilterModalProps = {
  options: ProductSearchFilters;
  isVisible: boolean;
  onClose: () => void;
  value: SelectedProductFilters;
  onChange: (search: SelectedProductFilters) => void;
};

const ProductSearchFilterModal = ({
  options,
  isVisible,
  onClose,
  value,
  onChange,
}: ProductSearchFilterModalProps) => {
  const [updatedFilters, setUpdatedFilters] = useState(value);

  useEffect(() => {
    if (isVisible) {
      setUpdatedFilters(value);
    }
    // We want to ensure the params are up to date when the modal first comes into view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const resetSearch = () => {
    setUpdatedFilters({
      category_ids: [],
      manufacturer_ids: [],
      species: [],
      incentive_ids: [],
      rebate_ids: [],
      prescription_required: [],
    });
  };

  const applyFilters = () => {
    onClose();
    onChange({ ...updatedFilters });
  };

  const filterItems = PRODUCT_FILTER_DEFINITIONS.map((definition) => {
    const definitionOptions = definition.optionsFrom(options);
    const selected = definition.selectedFrom(updatedFilters);
    return {
      key: definition.key,
      label: definition.label,
      options: definitionOptions,
      value: selected,
      content: (
        <SearchFilter<FilterValue>
          options={definitionOptions}
          value={selected}
          onChange={(next) =>
            setUpdatedFilters({ ...updatedFilters, ...definition.toUpdate(next) })
          }
        />
      ),
    };
  });

  // Every dimension counts, hidden ones included: Reset is the only way to clear a value whose
  // dimension came back with no options.
  const filterCount = filterItems.flatMap<number | string | boolean>((item) => item.value).length;

  const accordionItems = filterItems.filter((item) => item.options.length);

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Apply Filters" onClose={onClose} />
        <ModalLayout.Content>
          <Accordion.Multi
            items={accordionItems}
            initialOpenKeys={accordionItems.map((item) => item.key)}
          />
        </ModalLayout.Content>
        <ModalLayout.Actions>
          <ModalLayout.Actions.Button
            variant="secondary"
            onClick={resetSearch}
            disabled={!filterCount}
          >
            Reset {toCount(filterCount, "Filter")}
          </ModalLayout.Actions.Button>
          <ModalLayout.Actions.Button onClick={applyFilters}>Apply</ModalLayout.Actions.Button>
        </ModalLayout.Actions>
      </ModalLayout>
    </Modal>
  );
};

export default ProductSearchFilterModal;
